import clsx from 'clsx';
import { isEmpty, isNil } from 'lodash';
import React, { useRef, useState } from 'react';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';

import * as topoData from '@/assets/topo/states-10m.json';
import { Loader } from '@/components-new/loader';
import { Overlay } from '@/components-new/overlay';
import { displayPdlStatus, PdlStatus, PdlStatusAppliesTo } from '@/features/coverage/types/pdl-status';
import { getSortableCoverageTag } from '@/features/drugs/types/coverage-tag';
import { Drug } from '@/features/drugs/types/drug';
import { DrugCoverage } from '@/features/drugs/types/drug-coverage';

const styles = {
  state: {
    colors: {
      nonPdl: 'fill-gray-300 hover:fill-gray-300/50',
      preferred: 'fill-secondary-600 hover:fill-secondary-600/50',
      nonPreferred: 'fill-salmon-400 hover:fill-salmon-300/50',
      noState: 'fill-primary-950 hover:fill-primary-950/50',
      pending: 'fill-peach-300 hover:fill-peach-300/50',
      none: 'fill-white hover:fill-gray-100'
    }
  },
  statusIndicator: {
    colors: {
      nonPdl: 'bg-gray-300',
      preferred: 'bg-secondary-600',
      nonPreferred: 'bg-salmon-400',
      noState: 'bg-primary-950',
      pending: 'bg-peach-300',
      none: 'bg-white'
    }
  }
};

/**
 * Retrieves the appropriate color for the PDL status based on the given statuses and colors.
 */
const getPdlStatusColor = (
  statuses: Array<PdlStatus | undefined>,
  colors: typeof styles.statusIndicator.colors | typeof styles.state.colors
) => {
  if (isNil(statuses) || isEmpty(statuses)) return colors.none;

  const pendingStatus = [PdlStatus.PENDING];
  const hasPendingPdlStatus = statuses.some(status => pendingStatus.includes(status as any));
  if (hasPendingPdlStatus) return colors.pending;

  const preferredStatuses = [PdlStatus.PREFERRED, PdlStatus.PREFERRED_NOT_LISTED];
  const hasPreferredStatuses = statuses.some(status => preferredStatuses.includes(status as any));

  if (hasPreferredStatuses) return colors.preferred;

  const nonPreferredStatus = [PdlStatus.NON_PREFERRED, PdlStatus.NON_PREFERRED_NOT_LISTED];
  const hasNonPreferredStatus = statuses.some(status => nonPreferredStatus.includes(status as any));

  if (hasNonPreferredStatus) return colors.nonPreferred;

  const nonPdlStatus = [PdlStatus.NON_PDL];
  const hasNonPdlStatus = statuses.some(status => nonPdlStatus.includes(status as any));
  if (hasNonPdlStatus) return colors.nonPdl;

  return colors.noState;
};

type Tooltip = {
  data: { stateName: string; statuses: (PdlStatusAppliesTo | undefined)[]; },
  position: { x: number; y: number; },
}

/**
 * Tooltip component for displaying additional state information.
 */
const StateTooltip = React.forwardRef<HTMLDivElement, { tooltip: Tooltip }>(
  ({ tooltip }, ref) => {
    return (
      <div
        ref={ref}
        className={
          clsx('fixed z-[9999] rounded-md bg-white px-4 py-2 shadow-md')
        }
        style={{
          top: tooltip.position.y,
          left: tooltip.position.x,
        }}
      >
        <p className="font-semibold">{tooltip.data?.stateName}</p>
        <div className="text-sm text-gray-500">
            {isEmpty(tooltip.data?.statuses) || tooltip.data?.statuses?.every(it => isEmpty(it))
              ? <>No coverage details.</> : (
                tooltip.data.statuses.map((status, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <div
                      className={clsx(
                        'size-3 rounded-full',
                        getPdlStatusColor([status?.pdlStatus], styles.statusIndicator.colors)
                      )}
                    />
                    {/*@ts-ignore*/}
                    {displayPdlStatus(status?.pdlStatus)} {status?.appliesTo}
                  </div>
                ))
              )
            }
          </div>
      </div>
    );
  });

/**
 * Individual item in a chart key.
 */
const ChartKeyItem = ({ keyColor, children }: { keyColor: string, children: React.ReactNode }) => {
  return (
    <div className="flex items-center gap-2">
      <div className={clsx('size-4 rounded-sm border border-zinc-700', keyColor)}/>
      {children}
    </div>
  );
};

/**
 * Chart key that displays the color-coded items.
 */
const ChartKey = () => {
  return (
    <ul className="mt-8 flex gap-2">
      <li>
        <ChartKeyItem keyColor={styles.statusIndicator.colors.preferred}>
          Preferred/Preferred (Not Listed)
        </ChartKeyItem>
      </li>
      <li>
        <ChartKeyItem keyColor={styles.statusIndicator.colors.nonPreferred}>
          Non-Preferred/Non-Preferred (Not Listed)
        </ChartKeyItem>
      </li>
      <li>
        <ChartKeyItem keyColor={styles.statusIndicator.colors.nonPdl}>
          Non-PDL
        </ChartKeyItem>
      </li>
      <li>
        <ChartKeyItem keyColor={styles.statusIndicator.colors.noState}>
          No State PDL
        </ChartKeyItem>
      </li>
      <li>
        <ChartKeyItem keyColor={styles.statusIndicator.colors.pending}>
          Pending
        </ChartKeyItem>
      </li>
    </ul>
  );
};

/**
 * Custom Puerto Rico SVG component
 */
const PuertoRicoSVG = ({
  className,
  onMouseEnter,
  onMouseMove,
  onMouseLeave
}: {
  className: string;
  onMouseEnter: (e: React.MouseEvent<SVGPathElement>) => void;
  onMouseMove: (e: React.MouseEvent<SVGPathElement>) => void;
  onMouseLeave: () => void;
}) => {
  return (
    <path
      className={className}
      d="M73.1 155.8l3.9 3.6 0.1 2.4-1.9 2.5-3.5 6.2-1.4 1.6-1.1 1.6-0.5 2.8-1.4 1-3.2 0.3-3.2-0.3-1.4-1-2.2-3.2-9.6-5.4-2.2-4.9 2.2-5.1 3.9-2.1 19.5-0.5 2 0.5z m846.6 0l-3-2.9-4.9 1.1-6.4 2.7-26.5 6.3-5.2 2.5-7-2.3-10.5 1.3-9.8-1.4-5.2-10 5.2-0.2 1.7 0.2 6.7-0.6 6.7-2.5 6.1-3.7 4.8-3.9 3.2-1.7 27-4.4 4.3 0.8 11.7 4.1 17.7 0.7 4.4 1.7 3.3 4.2-2.4 1.3-9.1-0.6-2.2 0.8-6.5 4.5-1.7 2-2.4 0z m23.3-58.5l-4.6-7.4-14.1-13.2 0.1-1.2 8.3-0.1 9.6 2 8.2 4 4 6.2-0.9 0.2-3.7-0.2 2.3 2.4-3.6 0.2-2.8-0.8-1.9-1.7-0.9-2.7-2.3 0 3.1 6 0.6 2.8-1.4 3.5z m-118.1 0l-1.4 4.4 2.4 2.5 2.7 1.2 4.7-2.3-0.9 3.3 0.9 2.6 4.1 5.5-3.5 2.5-0.5 3-3 0.3-3.3-5.2-3.8 0-1.2 3.8 3.3 4.4-3.2 2.8-4.1-0.7-5.6-2.9-5.9 2-6.2 3-6.7 1.6-5.5 2.5-5.9 5.8-11.9 18.6-1 9.4-4.7 6.1-1.7 1.1-7.8 1.7-1.8 2.2 3.5 10.4-6.6 6.1-12.7 7.8-17.5 6.1-7.6-0.1-7.7-1.7-6.9-0.6-4.1 0.8-9.9 4-2.4 0.3-10.3-0.3-7 1.8-19.7 10.5-4.2 0.4-3.7-0.2-3.3 0.3-2.7 1.7-1.4-2.2-0.5-0.9-0.2-1.7 3.7-1.1 0.9-1.7-1.4-2.2-3.2-2.3-6.5 5.9-3.9 1.1-4.8-1.2-9.3-0.9-3.2-0.9-1.7-2-1.3-2.4-1.7-2-4.3-2.3-3-0.7-5.5 0.4-7.8 1.8-9.7 6.7-6.8-0.8-6.6-5.1-5.5-6.5-5.7-3.7-6.3 1.3-4.8 0.7-3.5 1.8-4.9 0.9-11.1 0.7-3.6 1.8-3.4 2.3-4 0.8-5.4-2.7-2.3 1.6-2.5 1-2.3-4-5.8-0.5-13.8 1.9-2.5-0.7-10.1-4-13.9 0 0.3-4.8-3-1.5-4.5 1-4.3 2.7 0 2.6 4.6 0-3.9 5.4-6.6 3.6-7.8 2.3-7 1.1-10.5-0.4-5.2 0.5-3.7-0.3-1.8 0.2-1.4 1.4-0.5 2.5-0.2 3.3-6.3-0.7-4.8-3.5-7.6-8.1-6.8-0.4-7.6-1.3-8.2 0.2-4.8-1.6-4.7 4-5.5 3-5 1.2-4-0.6-11.7-1.2-7.4-4.1-3.6 2.9 2.3 8.1-3.6-0.2-4.9-7.5-1.8-3.4 0.2-9.8 8-3.8 2.4-1.3 3.1-1 2.6-1.4 1.1-2.6-1.3-1.9-3.3-1.7-4.4 0.8-4.4-1.9 0.3-6.6 2.3-3.4 3.2-2.3-1.3-3.6-1.4-6 3.6-3.7-0.2-11.4 3.4-12.9 2.6-2.8 2.8-2.7 2-4.1-2.3-3.8-4.3-3.9-5.3-17-3.2-6.6-7.6-1.5-6.1-1.5-5.3-10.5-3.3-5.2-2.8-6.4 0.7-2.5 9.6-2.8 7.7-3.3 6.4-2.7 4.8-3 8.4-6.7 0.4-7.9-3.5-6.2-0.5-9.1 6.2-8.1 16.9-4.9 18 0.5 12.1 5.5 35.5 7.3 35.1-4.6 4.8 0.6 12.6 4.4 5.1 0.4 13.2-3 19.1-1.8 9 1.2 9 3.2 5.5-1 21.7 0.2 13.4 4 28.7-5.8 26.5 8.1 15.4-4.6 17.9 5.8 3.8-2.9 4.4 0.7 2.4 3 1.3 2 2.8 1.5 3.3-1.9 2.6-3.6 3.4 1-1.1 4.7 6.5 3.4 1.1 3.5 2.2 4.5 5.1 0.3 11.1-3.4 0-2.7-5.5-0.7-6.2-3.1-4.9-4.9-4.1-4.3 22.2 7 5.5 1.2 3.9 1.3 5.4 1 4.8-2.2 3-4.6 7.7 1.7 15.4 4.4 5.3-3.5 4 0.8 4.2 3.1 10.9 2.5 5.9 3.2 6.1 6.5 3.7-1.7 2.5-3.6 2 0.8 0.4 1.7-1.7 2-1.4 2.8 2.6-0.6 3.5-1.4 2.1-1.4 5.8 8.3 5.7 3.1 4.4-3.1 9.1 7.1 11.8 1.7 5.8-4.3 2.7-2.6 2.9-1.9 2.7 0.7 0.1 2.5-3.3 17.5-0.7 14.9 1.4 4.2z"
      onMouseEnter={onMouseEnter}
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
    />
  );
};

/**
 * Represents a map chart showing the coverage PDL statuses across each state.
 */
export const PdlStateCoverageChart = ({
  drug,
  coverages,
  isLoading
}: { drug: Drug, coverages?: DrugCoverage[]; isLoading: boolean }) => {
  const [tooltip, setTooltip] = useState<Tooltip | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(document.createElement('div'));

  /**
   * Triggered on initial entry of the mouse over the state, setting the tooltip data and position.
   */
  const handleMouseEnter = ({
    clientX,
    clientY
  }: React.MouseEvent<SVGGElement | SVGPathElement>, stateName: string, statuses: (PdlStatusAppliesTo | undefined)[]) => {
    setTooltip({
      data: { stateName, statuses },
      position: { x: clientX, y: clientY },
    });
  };

  /**
   * Triggered when the mouse moves over the state, updates the tooltip position.
   */
  const handleMouseMove = (event: React.MouseEvent<SVGGElement | SVGPathElement>) => {
    const { clientX, clientY } = event;
    const offset = 12;

    const tooltipWidth = tooltipRef.current?.offsetWidth || 0;
    const tooltipHeight = tooltipRef.current?.offsetHeight || 0;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let tooltipX = clientX + offset;
    let tooltipY = clientY + offset;

    if (tooltipX + tooltipWidth > viewportWidth) {
      tooltipX = clientX - tooltipWidth - offset;
    }

    if (tooltipY + tooltipHeight > viewportHeight) {
      tooltipY = viewportHeight - tooltipHeight - offset;
    }

    setTooltip((prevState) => {
      if (!prevState) return prevState;
      return {
        ...prevState,
        position: { x: tooltipX, y: tooltipY }
      };
    });
  };

  /**
   * Triggered when the mouse exits the state, removes the current tooltip information.
   */
  const handleMouseLeave = () => {
    setTooltip(null);
  };

  // Find Puerto Rico coverage data
  const puertoRicoCoverages = coverages?.filter(coverage =>
    coverage.state.name === 'Puerto Rico') ?? [];

  const puertoRicoStatusAndTargets = puertoRicoCoverages.map(coverage => {
    const tags = coverage?.coverageTags?.filter(tag => !tag.isDefault)
      .map(tag => getSortableCoverageTag(tag))
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map(sortableTag => sortableTag.coverageTag);

    const appliesTo = tags?.map((tag) =>
      // @ts-ignore
      tag.type == 'Indication' ? `for ${tag.name}` : tag.name
    ).join(', ');
    return { pdlStatus: coverage.pdlStatus, appliesTo: 'applies to '+ drug.name + ' ' + appliesTo } as PdlStatusAppliesTo;
  });

  return (
    <div>
      <ChartKey/>
      <div className="relative">
        {isEmpty(coverages) && (
          <Overlay>
            {isLoading && <Loader/>}
            {!isLoading && <p>No state coverage</p>}
          </Overlay>
        )}
        <div className="relative">
          <ComposableMap projection="geoAlbersUsa">
            <Geographies geography={topoData}>
              {({ geographies }) =>
                geographies.map(geo => {
                  const stateName = geo.properties.name;
                  const drugCoverages = coverages
                    ?.filter(coverage => coverage.state.name === stateName) ?? [];

                  const statusAndTargets = drugCoverages.map(coverage => {
                    const tags = coverage?.coverageTags?.filter(tag => !tag.isDefault)
                      .map(tag => getSortableCoverageTag(tag))
                      .sort((a, b) => a.sortOrder - b.sortOrder)
                      .map(sortableTag => sortableTag.coverageTag);

                    const appliesTo = tags?.map((tag) =>
                      // @ts-ignore
                      tag.type == 'Indication' ? `for ${tag.name}` : tag.name
                    ).join(', ');
                    return { pdlStatus: coverage.pdlStatus, appliesTo: 'applies to '+ drug.name + ' ' + appliesTo } as PdlStatusAppliesTo;
                  });

                  return (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      onMouseEnter={(e) => handleMouseEnter(e, stateName, statusAndTargets)}
                      onMouseMove={handleMouseMove}
                      onMouseLeave={handleMouseLeave}
                      style={{
                        default: { outline: 'none' },
                        hover: { outline: 'none' },
                        pressed: { outline: 'none' },
                      }}
                      className={clsx(
                        'stroke-zinc-700 transition-all',
                        getPdlStatusColor(statusAndTargets.map(status => status.pdlStatus), styles.state.colors)
                      )}
                    />
                  );
                })
              }
            </Geographies>
          </ComposableMap>

          {/* Puerto Rico SVG - positioned in the bottom right corner
              This SVG is used to represent Puerto Rico on the map. This is done because geoAlbersUsa does not include Puerto Rico in its projection and getting into custom projections
              is not worth the effort. Instead, we use a custom SVG path to represent Puerto Rico and position it manually.
          */}
          <div className="absolute bottom-[2%] right-[4%]" style={{ width: '10%', height: 'auto' }}>
            <svg viewBox="0 0 1000 234" preserveAspectRatio="xMidYMid meet" xmlns="http://www.w3.org/2000/svg">
              <PuertoRicoSVG
                className={clsx(
                  'stroke-zinc-700 stroke-[5px] transition-all',
                  getPdlStatusColor(
                    puertoRicoStatusAndTargets.map(status => status.pdlStatus),
                    styles.state.colors
                  )
                )}
                onMouseEnter={(e) => handleMouseEnter(e, 'Puerto Rico', puertoRicoStatusAndTargets)}
                onMouseMove={handleMouseMove}
                onMouseLeave={handleMouseLeave}
              />
              <text x="35" y="23" fontSize="6" textAnchor="middle">PR</text>
            </svg>
          </div>
        </div>
      </div>
      {tooltip && <StateTooltip tooltip={tooltip} ref={tooltipRef}/>}
    </div>
  );
};
